/*
 * The page layer: <BePage> / <BeShell> / <BeCols> / <BeCol> / <BeBand>. A class
 * name and at most one custom property each; the behaviour is layout/page.scss
 * and Notes/76. Polymorphic (`as`), since no accessibility contract rides on the
 * tag the way it does on a control (create.tsx).
 */

import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { BeRow } from "./Stack.tsx";

type PageCommon = BeCommon &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style"> & {
		children?: ReactNode;
		as?: ElementType;
	};

/** A COUNT of page units, never a length. Closed, so `pad={1.5}` is a compile error
 * rather than a silent 2 (Notes/93); the gutter ladder tops out at 5. */
export type BePageUnits = 0 | 1 | 2 | 3 | 4 | 5;

const units = (n: BePageUnits) => `calc(${n} * var(--be-page-unit))`;

/** The reflow ladder; `core/_breakpoints` is the one table. A name, not a number,
 * because a query needs a build-time length a call site cannot reach. */
export type BeBreakpoint =
	/** **224px** — a control band's floor. */
	| "xs"
	/** **448px** — a band holding something that needs real width. */
	| "sm"
	/** **672px** — Carbon's `md`, and where BeCols goes 1 ↔ 16 columns. */
	| "md"
	/** **1056px** — Carbon's `lg`. */
	| "lg"
	/** **1312px** — Carbon's `xlg`. */
	| "xlg";

/* ---- BePage ------------------------------------------------------------ */

export type BePageProps = PageCommon & {
	/** Inset on all sides, in PAGE UNITS (see `units` above). Default 2. */
	pad?: BePageUnits;
};

export const BePage = beComponent<BePageProps, HTMLElement>("BePage", (props, be) => {
	const { children, pad, as: Tag = "div", scale, className, style, ...rest } = props;
	const root = be("be-page");
	const css = pad != null ? ({ ...root.style, "--be-page-pad": units(pad) } as CSSProperties) : root.style;
	return (
		<Tag {...rest} {...root} style={css}>
			{children}
		</Tag>
	);
});

/* ---- BeShell ----------------------------------------------------------- */

/** What a rail does once it no longer fits beside Main. */
export type BeShellCollapse =
	/** Drop the rail entirely — for one that is navigation. */
	| "hide"
	/** Move it above Main and let it run to its content — for one that is content. */
	| "stack";

export type BeShellProps = PageCommon & {
	/** Lay the rail out at the trailing edge while it keeps leading in the DOM. */
	reverse?: boolean;
	/** Bind to the viewport and scroll Main inside itself, instead of flowing. */
	viewport?: boolean;
	/** Where the rail collapses. Default `md`: `railWidth + 2 * gutter + 672`. */
	at?: Extract<BeBreakpoint, "md" | "lg" | "xlg">;
	/** What happens there. Default `stack`. */
	collapse?: BeShellCollapse;
	/** Inset on all sides, in PAGE UNITS, wrapping the rail. Pad Main instead when
	 * the rail is chrome and sits flush. */
	pad?: BePageUnits;
	/** Take the responsive page gutter as that inset, so a rail lines up with Main. */
	gutter?: boolean;
	/** Space between the rail and Main, in PAGE UNITS. Default 0, i.e. flush. */
	gap?: BePageUnits;
};

/** The page frame: header and footer bands around a rail beside a main region. */
const BeShellRoot = beComponent<BeShellProps, HTMLElement>("BeShell", (props, be) => {
	const {
		children,
		reverse,
		viewport,
		at = "md",
		collapse = "stack",
		pad,
		gutter,
		gap,
		as: Tag = "div",
		scale,
		className,
		style,
		...rest
	} = props;
	const root = be(
		"be-shell",
		reverse && "is-reverse",
		viewport && "is-viewport",
		gutter && "is-gutter",
		`is-at-${at}`,
		`is-collapse-${collapse}`,
	);
	const css = {
		...root.style,
		...(pad != null ? { "--be-shell-pad": units(pad) } : null),
		...(gap != null ? { "--be-shell-gap": units(gap) } : null),
	} as CSSProperties;
	return (
		<Tag {...rest} {...root} style={css}>
			<div className="be-shell__frame">{children}</div>
		</Tag>
	);
});

/* Hardcoding `className` after a `{...rest}` spread silently DROPS the caller's. */
const cx = (own: string, extra?: string) => (extra ? `${own} ${extra}` : own);

export type BeShellBandProps = PageCommon & {
	/** Title text. Omit and pass children to build the band yourself. */
	title?: string;
	/** Trailing content: nav links, actions. */
	nav?: ReactNode;
};

/** A full-width band above rail and main. `title` renders the page's <h1>. */
function BeShellHeader({ title, nav, children, className, as: Tag = "header", ...rest }: BeShellBandProps & { className?: string }) {
	if (children) {
		return (
			<Tag {...rest} className={cx("be-shell__header", className)}>
				{children}
			</Tag>
		);
	}
	return (
		<BeRow as={Tag} justify="between" align="baseline" wrap {...rest} className={cx("be-shell__header", className)}>
			<h1 className="be-shell__title">{title}</h1>
			{nav != null && <nav>{nav}</nav>}
		</BeRow>
	);
}

/** A band below rail and main, outside Main's scroll region. */
function BeShellFooter({ children, className, as: Tag = "footer", ...rest }: PageCommon & { className?: string }) {
	return (
		<Tag {...rest} className={cx("be-shell__footer", className)}>
			{children}
		</Tag>
	);
}

export type BeShellRailProps = PageCommon & {
	/** Width in --be-px units. A content measurement, so a raw count. Default 280. */
	width?: number;
};

/** The fixed panel: a sidebar, a detail rail, a Deskbar. */
function BeShellRail({ width, children, className, as: Tag = "div", ...rest }: BeShellRailProps & { className?: string }) {
	const style = width != null ? ({ "--be-rail": `calc(${width} * var(--be-px))` } as CSSProperties) : undefined;
	return (
		<Tag {...rest} className={cx("be-shell__rail", className)} style={style}>
			{children}
		</Tag>
	);
}

/** The flexible region. IS a <BePage> -- padding and the grid container come from it. */
function BeShellMain({ children, pad, className, as: Tag = "div", ...rest }: BePageProps & { className?: string }) {
	return (
		<BePage as={Tag} pad={pad} {...rest} className={cx("be-shell__main", className)}>
			{children}
		</BePage>
	);
}

export const BeShell = Object.assign(BeShellRoot, {
	Header: BeShellHeader,
	Footer: BeShellFooter,
	Rail: BeShellRail,
	Main: BeShellMain,
});

/* ---- BeCols / BeCol ---------------------------------------------------- */

/* No `gap` prop, by choice: the gutter is the breakpoint map's page unit, and
 * `--be-cols-gap` is the escape (Notes/93). */
export type BeColsProps = PageCommon;

/** The page column grid: 16 columns above 672px of CONTAINER width, one below. The
 * count follows available width, so it is not a prop. */
export const BeCols = beComponent<BeColsProps, HTMLElement>("BeCols", (props, be) => {
	const { children, as: Tag = "div", scale, className, style, ...rest } = props;
	// two elements: a grid cannot query itself, so without the outer container a
	// <BeCols> outside a .be-page silently resolves to ONE column
	return (
		<Tag {...rest} {...be("be-cols")}>
			<div className="be-cols__grid">{children}</div>
		</Tag>
	);
});

export type BeColProps = PageCommon & {
	/** Width in columns, 1-16. `8` is half, `4` a quarter, `16` full. */
	span?: number;
};

/** A grid cell, and ALWAYS a containment context: one that is not sends a nested
 * grid to the OUTER container, with the wrong count and no error. */
export const BeCol = beComponent<BeColProps, HTMLElement>("BeCol", (props, be) => {
	const { children, span, as: Tag = "div", scale, className, style, ...rest } = props;
	// clamped rather than trusted: a span above the count silently overflows the
	// row, and there is no such thing as a useful `span 0`
	const n = span == null ? undefined : Math.max(1, Math.min(16, Math.round(span)));
	return (
		<Tag {...rest} {...be("be-col", n != null && `be-col-${n}`)}>
			{children}
		</Tag>
	);
});

/* ---- BeBand ------------------------------------------------------------ */

export type BeBandProps = PageCommon & {
	/** Lay the children out across a row instead of stacking them. */
	row?: boolean;
	/** How early a `row` band stacks. Default `xs`; raise it when a cell needs real
	 * width -- a text field beside a check box wants ~300px. */
	stack?: Extract<BeBreakpoint, "xs" | "sm" | "md">;
};

/** Shared block tracks, so a control can align against a COMPOSITE whose band
 * moves and has no text line to align on -- measured, `align="baseline"` is 17px
 * wrong there. A composite marks itself `is-composite` to adopt the tracks. */
export const BeBand = beComponent<BeBandProps, HTMLElement>("BeBand", (props, be) => {
	const { children, row, stack = "xs", as: Tag = "div", scale, className, style, ...rest } = props;
	return (
		<Tag {...rest} {...be("be-band", row && "is-row", `is-stack-${stack}`)}>
			{children}
		</Tag>
	);
});
